import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  bulkAction,
  reorderDocuments,
  updateDocument,
} from "../api/rfx-document-api";
import {
  bulkTemplateAction,
  reorderTemplateDocuments,
  updateTemplateDocument,
} from "../api/rfx-template-document-api";
import { UpdateRfxDocumentData } from "../types/rfx-document.types";
import { RFX_DOCUMENTS_QUERY_KEY } from "./useRfxDocuments";

export const useRfxDocumentMutations = (
  id: number,
  { isTemplate = false }: { isTemplate?: boolean } = {}
) => {
  const queryClient = useQueryClient();

  // Pick the API surface once — the BE keeps both endpoints isomorphic
  // (same request/response shape) so the mutation bodies are identical.
  const api = isTemplate
    ? {
        update: updateTemplateDocument,
        reorder: reorderTemplateDocuments,
        bulk: bulkTemplateAction,
      }
    : {
        update: updateDocument,
        reorder: reorderDocuments,
        bulk: bulkAction,
      };

  const queryKey = RFX_DOCUMENTS_QUERY_KEY(id, isTemplate);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey });
  };

  const updateMutation = useMutation({
    mutationFn: ({ docId, data }: { docId: number; data: UpdateRfxDocumentData }) =>
      api.update(id, docId, data),
    onSuccess: invalidate,
  });

  const reorderMutation = useMutation({
    mutationFn: (orderedIds: number[]) => api.reorder(id, orderedIds),
    onMutate: async (orderedIds: number[]) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (old: any[]) => {
        if (!old) {
          return old;
        }
        const indexMap = new Map(orderedIds.map((docId, idx) => [docId, idx]));
        return [...old].sort(
          (a, b) => (indexMap.get(a.id) ?? 0) - (indexMap.get(b.id) ?? 0)
        );
      });

      return { previous };
    },
    onError: (_err: unknown, _vars: unknown, context: any) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: invalidate,
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: (docIds: number[]) => api.bulk(id, "delete", docIds),
    onSuccess: invalidate,
  });

  const bulkChangeCategoryMutation = useMutation({
    mutationFn: ({
      docIds,
      categoryId,
    }: {
      docIds: number[];
      categoryId: number;
    }) => api.bulk(id, "change_category", docIds, categoryId),
    onSuccess: invalidate,
  });

  return {
    updateMutation,
    reorderMutation,
    bulkDeleteMutation,
    bulkChangeCategoryMutation,
  };
};
